import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
    Stack,
    TextField,
    IconButton,
    Tooltip,
    Button,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import ViewIcon from "@mui/icons-material/Visibility";
import { Download } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, differenceInDays } from "date-fns";
import BaseSelect from "components/Shared/Base/BaseSelect";
import {
    useLazyGetVendorListByRMorOnboardedQuery,
    useDownloadVendorListByRMorOnboardedExcelMutation,
} from "store/services/reportApi";

const VendorRMWiseReportDetail = () => {
    const { viewEntity } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const [errorMessage, setErrorMessage] = useState("");
    const [timeRange, setTimeRange] = useState(
        searchParams.get("timeRange") || "today"
    );
    const [customFromDate, setCustomFromDate] = useState(
        searchParams.get("fromDate") ? new Date(searchParams.get("fromDate")) : null
    );
    const [customToDate, setCustomToDate] = useState(
        searchParams.get("toDate") ? new Date(searchParams.get("toDate")) : null
    );
    // These are the dates actually used for fetching — only updated on "Apply" for custom range
    const [appliedFromDate, setAppliedFromDate] = useState(
        searchParams.get("fromDate") ? new Date(searchParams.get("fromDate")) : null
    );
    const [appliedToDate, setAppliedToDate] = useState(
        searchParams.get("toDate") ? new Date(searchParams.get("toDate")) : null
    );

    const groupBy = searchParams.get("groupBy") || "rm";
    const groupId = searchParams.get("groupId");
    const groupName = searchParams.get("groupName") || "Unassigned";

    const [
        fetchVendorList,
        {
            data: vendorListData,
            isLoading: isVendorListLoading,
            isFetching: isVendorListFetching,
            error: vendorListError,
        },
    ] = useLazyGetVendorListByRMorOnboardedQuery();

    const [downloadExcel] = useDownloadVendorListByRMorOnboardedExcelMutation();

    const getDateRange = useCallback((range, from, to) => {
        const today = moment().startOf("day");
        switch (range) {
            case "today":
                return {
                    fromDate: today.format("MM-DD-YYYY"),
                    toDate: today.endOf("day").format("MM-DD-YYYY"),
                };
            case "last7days":
                return {
                    fromDate: moment()
                        .subtract(6, "days")
                        .startOf("day")
                        .format("MM-DD-YYYY"),
                    toDate: today.endOf("day").format("MM-DD-YYYY"),
                };
            case "lastMonth":
                return {
                    fromDate: moment()
                        .subtract(1, "month")
                        .startOf("month")
                        .format("MM-DD-YYYY"),
                    toDate: moment()
                        .subtract(1, "month")
                        .endOf("month")
                        .format("MM-DD-YYYY"),
                };
            case "custom":
                if (from && to && moment(from).isValid() && moment(to).isValid()) {
                    if (differenceInDays(new Date(to), new Date(from)) > 90) {
                        setErrorMessage("Date range cannot exceed 90 days.");
                        return { fromDate: null, toDate: null };
                    }
                    return {
                        fromDate: moment(from).format("MM-DD-YYYY"),
                        toDate: moment(to).format("MM-DD-YYYY"),
                    };
                }
                setErrorMessage(
                    "Invalid date range. Ensure 'To' date is after 'From' date."
                );
                return { fromDate: null, toDate: null };
            default:
                return { fromDate: null, toDate: null };
        }
    }, []);

    const fetchVendorListData = useCallback(() => {
        if (!groupId || !["rm", "onboarded"].includes(groupBy)) {
            setErrorMessage("Invalid groupBy or groupId");
            return;
        }

        // For custom range, don't hit the API until both dates are applied via the Apply button
        if (timeRange === "custom" && (!appliedFromDate || !appliedToDate)) {
            return;
        }

        const { fromDate, toDate } = getDateRange(timeRange, appliedFromDate, appliedToDate);

        // Update URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.set("timeRange", timeRange);
        if (fromDate) newParams.set("fromDate", fromDate);
        else newParams.delete("fromDate");
        if (toDate) newParams.set("toDate", toDate);
        else newParams.delete("toDate");
        setSearchParams(newParams, { replace: true });

        const apiParams = {
            page: currentPage,
            limit: perPage,
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
            groupBy,
            groupId,
            fromDate,
            toDate,
        };

        fetchVendorList({ params: apiParams }).catch((err) => {
            setErrorMessage(
                err?.data?.message || "Failed to fetch vendor list"
            );
        });
    }, [currentPage, perPage, viewEntity, groupBy, groupId, fetchVendorList, timeRange, appliedFromDate, appliedToDate, getDateRange, searchParams, setSearchParams]);

    useEffect(() => {
        if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id && groupId) {
            fetchVendorListData();
        }
    }, [fetchVendorListData, groupId, viewEntity?.entity?._id, viewEntity?.entity?.account?._id]);

    // Use details directly, as they are objects
    const filteredData = Array.isArray(vendorListData?.data?.details)
        ? vendorListData.data.details
        : [];

    const columns = [
        {
            name: groupBy === "rm" ? "Relationship Manager" : "Onboarded By",
            selector: (row) => (groupBy === "rm" ? row.relationshipManager : row.onboardedBy),
            cell: (row) => (groupBy === "rm" ? row.relationshipManager : row.onboardedBy) || "N/A",
        },
        {
            name: "Shop Name",
            selector: (row) => row.shopName,
            cell: (row) => row.shopName || "N/A",
        },
        {
            name: "Shipment",
            selector: (row) => row.shipment,
            cell: (row) => row.shipment || 0,
        },
        {
            name: "Charge",
            selector: (row) => row.charge,
            cell: (row) => `Rs. ${row.charge?.toFixed(2) || "0.00"}`,
        },
        {
            name: "Details",
            cell: (row) => (
                <Link to={`/app/crm/vendor-profile/${row?.vendorId || row?._id}/detail`}>
                    <IconButton color="primary">
                        <ViewIcon />
                    </IconButton>
                </Link>
            ),
            ignoreRowClick: true,
        },
    ];

    const getRangeDisplay = () => {
        switch (timeRange) {
            case "today":
                return "Today";
            case "last7days":
                return "Last 7 Days";
            case "lastMonth":
                return "Last Month";
            case "custom":
                if (customFromDate && customToDate) {
                    return `Custom (${moment(customFromDate).format(
                        "MM/DD/YYYY"
                    )} - ${moment(customToDate).format("MM/DD/YYYY")})`;
                }
                return "Custom (Invalid Dates)";
            default:
                return "All";
        }
    };

    // Custom range is valid when both dates are set, ordered, and within 90 days
    const isCustomRangeValid =
        customFromDate &&
        customToDate &&
        moment(customFromDate).isValid() &&
        moment(customToDate).isValid() &&
        moment(customToDate).isSameOrAfter(moment(customFromDate), "day") &&
        differenceInDays(customToDate, customFromDate) <= 90;

    // Handle time range change
    const handleTimeRangeChange = (e) => {
        const value = e.target.value;
        setTimeRange(value);
        if (value !== "custom") {
            setCustomFromDate(null);
            setCustomToDate(null);
            setAppliedFromDate(null);
            setAppliedToDate(null);
        }
    };

    // Download the vendor list as Excel with the applied date filters
    const handleDownloadExcel = async () => {
        // For custom range, use the applied dates so the export matches the visible list
        if (timeRange === "custom" && (!appliedFromDate || !appliedToDate)) {
            return;
        }

        const { fromDate, toDate } = getDateRange(timeRange, appliedFromDate, appliedToDate);

        const apiParams = {
            groupBy,
            groupId,
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
            fromDate: !fromDate || fromDate === "null" ? undefined : fromDate,
            toDate: !toDate || toDate === "null" ? undefined : toDate,
            page: 1,
            limit: 10000,
        };

        try {
            const response = await downloadExcel({ params: apiParams }).unwrap();
            if (!response.blob) {
                throw new Error("No data returned from server");
            }

            const contentType =
                response.blob.type ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            if (!contentType.includes("spreadsheetml.sheet")) {
                throw new Error("Invalid content type received from server");
            }

            const groupLabel = groupBy === "rm" ? "RM" : "Onboarded_By";
            const dateSuffix =
                fromDate && fromDate !== "null" && toDate && toDate !== "null"
                    ? `_From_${fromDate}_to_${toDate}`
                    : `_${moment().format("YYYYMMDD")}`;

            const url = window.URL.createObjectURL(response.blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Vendor_List_By_${groupLabel}${dateSuffix}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setErrorMessage(
                error?.data?.message ||
                "Failed to download Excel file. Check filters or server status."
            );
            console.error("Failed to download Excel:", error);
        }
    };

    useAppMeta(`Vendor List by ${groupBy === "rm" ? "Relationship Manager" : "Onboarded By"}`);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <BreadcrumbLayout
                breadcrumbs={[
                    { title: "Reports", path: "/app/crm/reports/list" },
                    { title: "Vendor RM Wise Report", path: "/app/crm/reports/vendor-RM-report" },
                    { title: `Vendor List by ${groupBy === "rm" ? "Relationship Manager" : "Onboarded By"}` },
                ]}
            >
                <BreadcrumbLayout.Paper>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            px: 2,
                            pt: 2,
                            pb: 1,
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 2,
                            }}
                        >
                            <Typography variant="h6">
                                Vendor List for {groupBy === "rm" ? "Relationship Manager" : "Onboarded By"}: {groupName} - {getRangeDisplay()}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 2,
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                {/* Range */}
                                <BaseSelect
                                    label="Range"
                                    value={timeRange}
                                    onChange={handleTimeRangeChange}
                                    options={[
                                        { label: "Today", value: "today" },
                                        { label: "Last 7 Days", value: "last7days" },
                                        { label: "Last Month", value: "lastMonth" },
                                        { label: "Custom", value: "custom" },
                                    ]}
                                    getOptionLabel={(opt) => opt.label}
                                    getOptionValue={(opt) => opt.value}
                                    size="small"
                                    sx={{ minWidth: 140 }}
                                />

                                {/* Custom Date Pickers */}
                                {timeRange === "custom" && (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DatePicker
                                            label="From"
                                            value={customFromDate}
                                            onChange={(newValue) => {
                                                setCustomFromDate(newValue);
                                                if (
                                                    customToDate &&
                                                    differenceInDays(customToDate, newValue) > 90
                                                ) {
                                                    setCustomToDate(addDays(newValue, 90));
                                                    setErrorMessage(
                                                        "Date range adjusted to maximum 90 days."
                                                    );
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    sx={{ width: 140 }}
                                                />
                                            )}
                                            maxDate={customToDate || new Date()}
                                            disableFuture
                                        />
                                        <DatePicker
                                            label="To"
                                            value={customToDate}
                                            onChange={(newValue) => {
                                                setCustomToDate(newValue);
                                                if (
                                                    customFromDate &&
                                                    differenceInDays(newValue, customFromDate) > 90
                                                ) {
                                                    setCustomFromDate(addDays(newValue, -90));
                                                    setErrorMessage(
                                                        "Date range adjusted to maximum 90 days."
                                                    );
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    sx={{ width: 140 }}
                                                />
                                            )}
                                            minDate={customFromDate}
                                            maxDate={new Date()}
                                            disableFuture
                                        />
                                        <Tooltip
                                            title={
                                                isCustomRangeValid
                                                    ? "Apply date range"
                                                    : "Select a valid range (max 90 days)"
                                            }
                                        >
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={!isCustomRangeValid}
                                                    onClick={() => {
                                                        setAppliedFromDate(customFromDate);
                                                        setAppliedToDate(customToDate);
                                                    }}
                                                >
                                                    Apply
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                )}

                                {/* Download */}
                                <Tooltip title="Download Excel">
                                    <span>
                                        <IconButton
                                            onClick={handleDownloadExcel}
                                            color="primary"
                                            disabled={
                                                isVendorListLoading ||
                                                isVendorListFetching ||
                                                !filteredData.length
                                            }
                                        >
                                            <Download />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>

                    {isVendorListLoading || isVendorListFetching ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : vendorListError ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography variant="body1" color="error">
                                Error: {vendorListError?.data?.message || "Failed to load vendor list"}
                            </Typography>
                        </Box>
                    ) : filteredData.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                No vendors found for the selected {groupBy === "rm" ? "Relationship Manager" : "Onboarded By"}.
                            </Typography>
                        </Box>
                    ) : (
                        <AppTable
                            columns={columns}
                            data={filteredData}
                            progressPending={isVendorListLoading || isVendorListFetching}
                            paginationServer
                            paginationPerPage={perPage}
                            paginationTotalRows={vendorListData?.data?.totalItems ?? 0}
                            paginationDefaultPage={currentPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                        />
                    )}

                    <Snackbar
                        open={!!errorMessage}
                        autoHideDuration={6000}
                        onClose={() => setErrorMessage("")}
                    >
                        <Alert severity="error" onClose={() => setErrorMessage("")}>
                            {errorMessage}
                        </Alert>
                    </Snackbar>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        </LocalizationProvider>
    );
};

export default VendorRMWiseReportDetail;